import { useBusinessEntityDocuments } from '@commenda/api';
import { downloadDocument } from '@commenda/api/document';
import {
  Button,
  DocViewerComponent,
  Icons,
  useToggle,
} from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import { FileBasic } from '@commenda/types/src/file/types';
import { documentName, getFileTypeByUrl } from '@commenda/utils';
import { downloadDocumentLocally } from '@commenda/utils/documents/download-document';
import { getSignedUrlByFileId } from '@commenda/utils/files/uploadFile';
import { LegacyDocumentType } from '@prisma/client';
import { useEffect, useState } from 'react';
import { FileUploadModal } from './FileUploadModal';

const UploadDocumentSection = ({
  documentType,
  title,
  description,
  businessEntity,
}: {
  title: string;
  description: string;
  businessEntity: IBusinessEntity;
  documentType: LegacyDocumentType;
}) => {
  const [file, setFile] = useState<FileBasic>();
  const [documentLink, setDocumentLink] = useState('');

  const uploadModalVisibility = useToggle();
  const downloadLoaderVisibility = useToggle();

  const { data: businessEntityDocuments, mutate: mutateDocuments } =
    useBusinessEntityDocuments({
      businessEntityId: businessEntity.id,
    });

  const uploadedDocuments =
    businessEntityDocuments?.documents.filter(
      (document) => document.documentSubType === documentType
    ) ?? [];

  const uploadedDocument =
    uploadedDocuments.length > 0
      ? uploadedDocuments[uploadedDocuments.length - 1]
      : null;

  useEffect(() => {
    let mounted = true;

    const fetchSignedUrl = async () => {
      try {
        if (uploadedDocument?.id) {
          // Oth Index here is fine in the agent app since, they are uploading these documents for the first time
          const link = await getSignedUrlByFileId(uploadedDocument?.id, null);
          if (mounted) {
            setDocumentLink(link);
          }
        }
      } catch (error) {
        console.error('Error fetching signed URL:', error);
      }
    };

    fetchSignedUrl();

    return () => {
      mounted = false;
    };
  }, [uploadedDocument]);

  async function downloadFile() {
    try {
      downloadLoaderVisibility.show();
      const { fileName } = uploadedDocument;
      const fileType = getFileTypeByUrl(documentLink);

      const blob = await downloadDocument({
        fileId: uploadedDocument.id as number,
        fileType,
      });

      await downloadDocumentLocally({
        blob,
        fileName,
        fileType,
      });
    } catch (error) {
      console.error(error);
    } finally {
      downloadLoaderVisibility.hide();
    }
  }

  return (
    <div className="flex flex-col h-full gap-4xl">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex flex-col w-full gap-xs">
          <h1 className="txt-large-semibold">{title}</h1>
          <p className="text-sm text-text-secondary">{description}</p>
        </div>
        <div className="flex justify-end gap-2">
          {uploadedDocument ? (
            <Button
              variant="secondary"
              onClick={uploadModalVisibility.toggle}
              size="sm"
            >
              <Icons.Upload01 className="size-4" />
              Reupload
            </Button>
          ) : null}
        </div>
      </div>

      {uploadedDocument ? (
        <div className="border border-border-secondary p-5 w-full h-[80vh] overflow-y-auto">
          <DocViewerComponent uri={documentLink} />
        </div>
      ) : (
        <div className="relative">
          <div className="absolute bottom-16 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 flex gap-4">
            <Button
              onClick={uploadModalVisibility.toggle}
              variant="secondary"
              size="sm"
            >
              <Icons.Upload01 className="size-4" />
              Upload
            </Button>
          </div>

          <div className="rounded-2xl bg-bg-secondary h-[400px] mt-6 flex flex-col items-center justify-center">
            <div className="flex size-12 rounded-lg bg-white border border-icon-featured-modern-border items-center justify-center mb-5">
              <Icons.File05 className="size-5" />
            </div>
            <div className="mt-4 flex flex-col gap-1">
              <h3 className="txt-medium-bold text-center text-text-primary">
                No document uploaded
              </h3>
              <p className="text-center">
                Once a document has been uploaded, a preview will load here.
              </p>
            </div>
          </div>
        </div>
      )}
      <FileUploadModal
        companyId={businessEntity.generalCorporationInfo?.companyId}
        file={file}
        setFile={setFile}
        isOpen={uploadModalVisibility.visible}
        setIsOpen={uploadModalVisibility.hide}
        uploadedDocument={uploadedDocument}
        documentType={documentType}
        title={`Upload ${documentName(documentType)}`}
        onUploadSuccess={() => {
          mutateDocuments();
        }}
        accept={{
          'application/pdf': ['.pdf', '.xlsx', '.docx', '.csv'],
        }}
        corporationId={businessEntity.id}
      />
    </div>
  );
};

export { UploadDocumentSection };
